import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Home,
  Image,
  LogOut,
  Mail,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import BookingEngine from '../sections/BookingEngine';
import type { Booking, Property } from '../../types';

const PROPERTY_ID = 'villa_kourtsoudi_01';

interface PropertyFormState {
  base_price_low: number;
  base_price_mid: number;
  base_price_high: number;
  min_nights: number;
  max_guests: number;
}

interface CalendarEntry {
  id: string;
  date: string;
  type: 'booking' | 'blocked';
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  source?: string;
  title: string;
  color: string;
}

interface Note {
  id: string;
  content: string;
  created_at?: Timestamp | { toDate: () => Date } | string;
  done: boolean;
}

interface EmailItem {
  uid: number;
  from: string;
  subject: string;
  date: string;
  snippet: string;
  unread: boolean;
}

const initialForm: PropertyFormState = {
  base_price_low: 0,
  base_price_mid: 0,
  base_price_high: 0,
  min_nights: 1,
  max_guests: 2,
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('properties');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [property, setProperty] = useState<Property | null>(null);
  const [propertyForm, setPropertyForm] = useState<PropertyFormState>(initialForm);
  const [propertyLoading, setPropertyLoading] = useState(true);
  const [savingProperty, setSavingProperty] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [calendarEntries, setCalendarEntries] = useState<CalendarEntry[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarDay, setCalendarDay] = useState<string | null>(null);
  const [emails, setEmails] = useState<EmailItem[]>([]);
  const [emailPage, setEmailPage] = useState(1);
  const [emailPages, setEmailPages] = useState(0);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState<number | null>(null);
  const [emailReader, setEmailReader] = useState<{ uid: number; from: string; to: string; subject: string; date: string; body: string } | null>(null);
  const [emailModalMode, setEmailModalMode] = useState<'read' | 'compose'>('read');
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeText, setComposeText] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailModalError, setEmailModalError] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [savingNote, setSavingNote] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const monthGrid = useMemo(() => {
    const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const start = addDays(firstDay, -firstDay.getDay());
    const days: Date[] = [];
    for (let index = 0; index < 42; index += 1) {
      days.push(addDays(start, index));
    }
    return days;
  }, [calendarMonth]);

  const loadProperty = async () => {
    setPropertyLoading(true);
    try {
      const snap = await getDoc(doc(db, 'properties', PROPERTY_ID));
      if (!snap.exists()) {
        throw new Error('Property record not found');
      }
      const propertyData = snap.data() as Property;
      setProperty(propertyData);
      setPropertyForm({
        base_price_low: Number(propertyData.base_price_low || 0),
        base_price_mid: Number(propertyData.base_price_mid || 0),
        base_price_high: Number(propertyData.base_price_high || 0),
        min_nights: Number(propertyData.min_nights || 1),
        max_guests: Number(propertyData.max_guests || 2),
      });
    } catch (error) {
      console.error('Error loading property:', error);
      setToast({ type: 'error', message: 'Could not load property settings.' });
    } finally {
      setPropertyLoading(false);
    }
  };

  const loadCalendar = async () => {
    setCalendarLoading(true);
    try {
      const [bookingSnap, rangeSnap] = await Promise.all([
        getDocs(query(collection(db, 'bookings'), where('property_id', '==', PROPERTY_ID))),
        getDocs(query(collection(db, 'blockedRanges'), where('property_id', '==', PROPERTY_ID))),
      ]);

      const entries: CalendarEntry[] = [];
      bookingSnap.docs.forEach((document) => {
        const booking = document.data() as Booking;
        entries.push({
          id: document.id,
          date: booking.checkin,
          type: 'booking',
          status: booking.status,
          title: `${booking.status}: ${booking.guest_name}`,
          color: booking.status === 'APPROVED' ? 'bg-blue-100 text-blue-900' : 'bg-yellow-100 text-yellow-900',
        });
        if (booking.checkout > booking.checkin) {
          for (let date = new Date(`${booking.checkin}T00:00:00`); date < new Date(`${booking.checkout}T00:00:00`); date.setDate(date.getDate() + 1)) {
            entries.push({
              id: `${document.id}-${formatDate(date)}`,
              date: formatDate(date),
              type: 'booking',
              status: booking.status,
              title: `${booking.status}: ${booking.guest_name}`,
              color: booking.status === 'APPROVED' ? 'bg-blue-100 text-blue-900' : 'bg-yellow-100 text-yellow-900',
            });
          }
        }
      });

      rangeSnap.docs.forEach((document) => {
        const data = document.data();
        const start = new Date(`${data.start}T00:00:00`);
        const end = new Date(`${data.end}T00:00:00`);
        for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
          entries.push({
            id: `${document.id}-${formatDate(date)}`,
            date: formatDate(date),
            type: 'blocked',
            source: String(data.source || 'manual').toLowerCase(),
            title: `Blocked · ${String(data.source || 'manual').toLowerCase()}`,
            color: 'bg-gray-200 text-gray-800',
          });
        }
      });

      setCalendarEntries(entries);
    } catch (error) {
      console.error('Error loading calendar:', error);
      setToast({ type: 'error', message: 'Could not load the calendar.' });
    } finally {
      setCalendarLoading(false);
    }
  };

  const loadEmails = useCallback(async (page = emailPage) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setToast({ type: 'error', message: 'Admin authentication is required.' });
      return;
    }

    setEmailLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/.netlify/functions/gmail', {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'list', page }),
      });
      const data = await response.json();
      if (!response.ok || !data.emails) {
        throw new Error(data.error || 'Unable to load emails');
      }
      setEmails(data.emails);
      setEmailPage(data.page || 1);
      setEmailPages(data.pages || 0);
      setEmailTotal(data.total || 0);
    } catch (error) {
      console.error('Error loading emails:', error);
      const message = error instanceof Error ? error.message : 'Could not load emails.';
      setToast({ type: 'error', message: message });
    } finally {
      setEmailLoading(false);
    }
  }, [emailPage, setEmailLoading, setEmails, setEmailPage, setEmailPages, setEmailTotal, setToast]);

  const loadEmailReader = async (uid: number) => {
    setSelectedUid(uid);
    setEmailModalMode('read');
    setEmailReader(null);
    setEmailModalError(null);
    setEmailLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Admin authentication is required.');
      const token = await currentUser.getIdToken();
      const response = await fetch('/.netlify/functions/gmail', {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'read', uid }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to load email.');
      setEmailReader(data.message);
      setReplyText('');
    } catch (error) {
      console.error('Error opening email:', error);
      const message = error instanceof Error ? error.message : 'Could not open email.';
      setEmailModalError(message);
    } finally {
      setEmailLoading(false);
    }
  };

  const closeEmailModal = () => {
    setSelectedUid(null);
    setEmailReader(null);
    setEmailModalMode('read');
    setEmailModalError(null);
    setReplyText('');
    setComposeTo('');
    setComposeSubject('');
    setComposeText('');
  };

  const loadNotes = async () => {
    setNotesLoading(true);
    try {
      const q = query(
        collection(db, 'notes_tasks'),
        where('property_id', '==', PROPERTY_ID),
        orderBy('created_at', 'desc')
      );
      const snapshot = await getDocs(q);
      setNotes(snapshot.docs.map((document) => ({
        id: document.id,
        ...(document.data() as Omit<Note, 'id'>),
      })));
    } catch (error) {
      console.error('Error loading notes:', error);
      setToast({ type: 'error', message: 'Could not load notes.' });
    } finally {
      setNotesLoading(false);
    }
  };

  useEffect(() => {
    loadProperty();
    loadCalendar();
    loadNotes();
  }, []);

  useEffect(() => {
    if (toast) {
      window.setTimeout(() => setToast(null), 4000);
    }
  }, [toast]);

  useEffect(() => {
    if (activeTab === 'emails') {
      loadEmails(1);
    }
  }, [activeTab, loadEmails]);

  const handlePropertySave = async () => {
    if (!property) return;
    setSavingProperty(true);
    try {
      const updates: Record<string, number> = {
        base_price_low: propertyForm.base_price_low,
        base_price_mid: propertyForm.base_price_mid,
        base_price_high: propertyForm.base_price_high,
        min_nights: propertyForm.min_nights,
        max_guests: propertyForm.max_guests,
      };
      await updateDoc(doc(db, 'properties', PROPERTY_ID), updates);
      setProperty({ ...property, ...updates });
      setToast({ type: 'success', message: 'Property settings saved.' });
    } catch (error) {
      console.error('Error saving property:', error);
      setToast({ type: 'error', message: 'Could not save property settings.' });
    } finally {
      setSavingProperty(false);
    }
  };

  const handleEmailDelete = async (uid: number) => {
    setDeleting(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Admin authentication is required.');
      const token = await currentUser.getIdToken();
      const response = await fetch('/.netlify/functions/gmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'delete', uid }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to delete email.');
      closeEmailModal();
      await loadEmails(emailPage);
      setToast({ type: 'success', message: 'Email deleted.' });
    } catch (error) {
      console.error('Error deleting email:', error);
      setToast({ type: 'error', message: error instanceof Error ? error.message : 'Could not delete email.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleEmailDraftSave = async () => {
    const subject = emailModalMode === 'compose' ? composeSubject.trim() : emailReader?.subject || '';
    const body = emailModalMode === 'compose' ? composeText.trim() : replyText.trim();
    const to = emailModalMode === 'compose' ? composeTo.trim() : emailReader?.from || '';
    if (!body && emailModalMode === 'read') return;
    setSavingDraft(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Admin authentication is required.');
      const token = await currentUser.getIdToken();
      const response = await fetch('/.netlify/functions/gmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'draft',
          to: emailModalMode === 'compose' ? to : emailReader?.from,
          subject,
          text: body,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to save draft.');
      if (emailModalMode === 'compose') {
        setComposeText('');
        setComposeSubject('');
        setComposeTo('');
      } else {
        setReplyText('');
      }
      setToast({ type: 'success', message: 'Draft saved to Gmail.' });
    } catch (error) {
      console.error('Error saving draft:', error);
      setToast({ type: 'error', message: error instanceof Error ? error.message : 'Could not save draft.' });
    } finally {
      setSavingDraft(false);
    }
  };

  const handleEmailReply = async () => {
    if (!emailReader || !replyText.trim()) return;
    setReplying(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Admin authentication is required.');
      const token = await currentUser.getIdToken();
      const response = await fetch('/.netlify/functions/gmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'reply', uid: emailReader.uid, replyText }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to send reply.');
      setReplyText('');
      setToast({ type: 'success', message: 'Reply sent.' });
    } catch (error) {
      console.error('Error replying to email:', error);
      setToast({ type: 'error', message: error instanceof Error ? error.message : 'Could not send reply.' });
    } finally {
      setReplying(false);
    }
  };

  const handleOpenEmailWindow = () => {
    if (!emailReader) return;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${emailReader.subject}</title><style>body{font-family:Arial,sans-serif;margin:24px;color:#111}p{line-height:1.6}pre{white-space:pre-wrap;word-break:break-word}</style></head><body><h1>${emailReader.subject}</h1><p><strong>From:</strong> ${emailReader.from}<br><strong>To:</strong> ${emailReader.to}<br><strong>Date:</strong> ${emailReader.date}</p><hr><pre>${emailReader.body || 'This message has no readable text body.'}</pre></body></html>`;
    const popup = window.open('', '_blank', 'noopener,noreferrer');
    if (!popup) {
      setToast({ type: 'error', message: 'The email window was blocked by the browser.' });
      return;
    }
    popup.document.write(html);
    popup.document.close();
  };

  const handleEmailSend = async () => {
    if (!composeTo.trim() || !composeText.trim()) return;
    setComposeSending(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Admin authentication is required.');
      const token = await currentUser.getIdToken();
      const response = await fetch('/.netlify/functions/gmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'send',
          to: composeTo.trim(),
          subject: composeSubject.trim() || '(no subject)',
          text: composeText.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to send email.');
      closeEmailModal();
      setToast({ type: 'success', message: 'Email sent.' });
    } catch (error) {
      console.error('Error sending new email:', error);
      setToast({ type: 'error', message: error instanceof Error ? error.message : 'Could not send email.' });
    } finally {
      setComposeSending(false);
    }
  };

  const handleNoteAdd = async () => {
    const content = noteInput.trim();
    if (!content) return;
    setSavingNote(true);
    try {
      await addDoc(collection(db, 'notes_tasks'), {
        property_id: PROPERTY_ID,
        content,
        created_at: serverTimestamp(),
        done: false,
      });
      setNoteInput('');
      await loadNotes();
      setToast({ type: 'success', message: 'Note added.' });
    } catch (error) {
      console.error('Error adding note:', error);
      setToast({ type: 'error', message: 'Could not add note.' });
    } finally {
      setSavingNote(false);
    }
  };

  const handleNoteToggle = async (noteId: string, done: boolean) => {
    try {
      await updateDoc(doc(db, 'notes_tasks', noteId), { done: !done });
      setNotes((current) => current.map((note) =>
        note.id === noteId ? { ...note, done: !done } : note
      ));
    } catch (error) {
      console.error('Error updating note:', error);
      setToast({ type: 'error', message: 'Could not update note.' });
    }
  };

  const handleNoteDelete = async (noteId: string) => {
    try {
      await deleteDoc(doc(db, 'notes_tasks', noteId));
      setNotes((current) => current.filter((note) => note.id !== noteId));
      setToast({ type: 'success', message: 'Note deleted.' });
    } catch (error) {
      console.error('Error deleting note:', error);
      setToast({ type: 'error', message: 'Could not delete note.' });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const selectedDayEntries = calendarDay
    ? calendarEntries.filter((entry) => entry.date === calendarDay)
    : [];

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <Home className="h-8 w-8 text-primary-600" />
                <span className="ml-2 text-xl font-semibold text-gray-900">Admin Dashboard</span>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <button onClick={() => setActiveTab('properties')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'properties' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}><Image className="mr-2 h-4 w-4" />Properties</button>
                <button onClick={() => setActiveTab('calendar')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'calendar' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}><Calendar className="mr-2 h-4 w-4" />Calendar</button>
                <button onClick={() => setActiveTab('bookings')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'bookings' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}><Calendar className="mr-2 h-4 w-4" />Bookings</button>
                <button onClick={() => setActiveTab('emails')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'emails' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}><Mail className="mr-2 h-4 w-4" />Emails</button>
                <button onClick={() => setActiveTab('notes')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'notes' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}><FileText className="mr-2 h-4 w-4" />Notes</button>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/" className="py-2 px-2 font-medium text-gray-500 rounded hover:bg-primary-500 hover:text-white transition duration-300">View Website</Link>
              <button onClick={handleLogout} className="flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 focus:outline-none transition duration-150 ease-in-out"><LogOut className="mr-2 h-4 w-4" />Logout</button>
            </div>
          </div>
        </div>
      </nav>

      {toast && (
        <div className="fixed right-4 top-20 z-50 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-lg">
          <p className={toast.type === 'success' ? 'text-green-700' : 'text-red-700'}>{toast.message}</p>
        </div>
      )}

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'bookings' && <BookingEngine isAdminView />}

        {activeTab === 'properties' && (
          <section className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Property Settings</h3>
              <p className="mt-1 text-sm text-gray-500">Edit the pricing and occupancy limits for {PROPERTY_ID}.</p>
            </div>
            <div className="border-t border-gray-200 px-4 py-6 sm:p-6">
              {propertyLoading ? <p className="text-sm text-gray-500">Loading property…</p> : (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-5">
                  <label className="block text-sm font-medium text-gray-700">Low season price<input type="number" min="0" step="1" value={propertyForm.base_price_low} onChange={(event) => setPropertyForm((current) => ({ ...current, base_price_low: Number(event.target.value) }))} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <label className="block text-sm font-medium text-gray-700">Mid season price<input type="number" min="0" step="1" value={propertyForm.base_price_mid} onChange={(event) => setPropertyForm((current) => ({ ...current, base_price_mid: Number(event.target.value) }))} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <label className="block text-sm font-medium text-gray-700">High season price<input type="number" min="0" step="1" value={propertyForm.base_price_high} onChange={(event) => setPropertyForm((current) => ({ ...current, base_price_high: Number(event.target.value) }))} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <label className="block text-sm font-medium text-gray-700">Minimum nights<input type="number" min="1" value={propertyForm.min_nights} onChange={(event) => setPropertyForm((current) => ({ ...current, min_nights: Number(event.target.value) }))} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <label className="block text-sm font-medium text-gray-700">Maximum guests<input type="number" min="1" value={propertyForm.max_guests} onChange={(event) => setPropertyForm((current) => ({ ...current, max_guests: Number(event.target.value) }))} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <button onClick={handlePropertySave} disabled={savingProperty || propertyLoading} className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400"><Save className="mr-2 h-4 w-4" />{savingProperty ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'calendar' && (
          <section className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div><h3 className="text-lg font-medium text-gray-900">Booking Calendar</h3><p className="mt-1 text-sm text-gray-500">PENDING and APPROVED bookings, plus imported blocked ranges.</p></div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} className="rounded-md border border-gray-300 p-2 text-gray-600 hover:bg-gray-100"><ChevronLeft className="h-4 w-4" /></button>
                <span className="min-w-32 text-center text-sm font-medium text-gray-700">{calendarMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
                <button type="button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} className="rounded-md border border-gray-300 p-2 text-gray-600 hover:bg-gray-100"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
              <div className="flex flex-wrap gap-3 text-xs text-gray-600"><span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-yellow-200" />PENDING</span><span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-blue-100" />APPROVED</span><span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-gray-200" />Blocked range</span></div>
            </div>
            <div className="p-4 sm:p-6">
              {calendarLoading ? <p className="text-sm text-gray-500">Loading calendar…</p> : (
                <div>
                  <div className="grid grid-cols-7 border border-gray-200 text-center text-xs font-medium uppercase tracking-wide text-gray-500"><div className="border-r border-gray-200 py-2">Sun</div><div className="border-r border-gray-200 py-2">Mon</div><div className="border-r border-gray-200 py-2">Tue</div><div className="border-r border-gray-200 py-2">Wed</div><div className="border-r border-gray-200 py-2">Thu</div><div className="border-r border-gray-200 py-2">Fri</div><div className="py-2">Sat</div></div>
                  <div className="grid grid-cols-7 border-x border-b border-gray-200">
                    {monthGrid.map((day) => {
                      const dayKey = formatDate(day);
                      const dayEntries = calendarEntries.filter((entry) => entry.date === dayKey);
                      const inCurrentMonth = day.getMonth() === calendarMonth.getMonth();
                      return (
                        <button key={dayKey} type="button" onClick={() => setCalendarDay(dayKey)} className={`min-h-28 border-b border-r border-gray-200 p-1 text-left transition hover:bg-gray-50 ${!inCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white text-gray-900'} ${calendarDay === dayKey ? 'ring-2 ring-primary-500' : ''}`}>
                          <span className="mb-1 block text-xs font-medium">{day.getDate()}</span>
                          <div className="space-y-1">
                            {dayEntries.slice(0, 3).map((entry) => <span key={entry.id} title={entry.title} className={`block truncate rounded px-1 text-[10px] font-medium ${entry.color}`}>{entry.status || entry.source}</span>)}
                            {dayEntries.length > 3 && <span className="block text-[10px] text-gray-500">+{dayEntries.length - 3} more</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {calendarDay && (
              <div className="border-t border-gray-200 p-4 sm:p-6">
                <h4 className="mb-3 text-sm font-semibold text-gray-900">{calendarDay}</h4>
                <div className="space-y-2">
                  {selectedDayEntries.length ? selectedDayEntries.map((entry) => <div key={entry.id} className={`rounded border px-3 py-2 text-sm ${entry.color}`} title={entry.type === 'blocked' ? `Source: ${entry.source}` : `Status: ${entry.status}`}><span className="font-medium">{entry.type === 'blocked' ? 'Blocked range' : entry.status}</span><span className="mx-2">•</span><span>{entry.title}</span></div>) : <p className="text-sm text-gray-500">No bookings or blocked ranges.</p>}
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'emails' && (
          <section className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6"><h3 className="text-lg font-medium text-gray-900">Gmail Inbox</h3><p className="mt-1 text-sm text-gray-500">Server-side pagination through the IMAP mailbox.</p></div>
            <div className="border-t border-gray-200 p-4 sm:p-6">
              <div className="mb-4 flex justify-end"><button onClick={() => { setSelectedUid(null); setEmailModalMode('compose'); setEmailModalError(null); setComposeTo(''); setComposeSubject(''); setComposeText(''); }} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">Compose</button></div>
              {emailLoading ? <p className="text-sm text-gray-500">Loading inbox…</p> : (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm"><thead className="bg-gray-50"><tr><th className="px-3 py-3 text-left font-medium text-gray-700">Sender</th><th className="px-3 py-3 text-left font-medium text-gray-700">Subject</th><th className="px-3 py-3 text-left font-medium text-gray-700">Date</th><th className="px-3 py-3 text-left font-medium text-gray-700">Unread</th></tr></thead><tbody className="divide-y divide-gray-200">{emails.length ? emails.map((email) => <tr key={email.uid} onClick={() => void loadEmailReader(email.uid)} className={email.unread ? 'cursor-pointer bg-blue-50/50 hover:bg-blue-100' : 'cursor-pointer bg-white hover:bg-gray-50'}><td className="px-3 py-3 text-gray-900">{email.from}</td><td className="px-3 py-3"><div className="font-medium text-gray-900">{email.subject}</div></td><td className="px-3 py-3 text-gray-500">{new Date(email.date).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td><td className="px-3 py-3"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${email.unread ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>{email.unread ? 'New' : 'Read'}</span></td></tr>) : <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500">No emails found.</td></tr>}</tbody></table>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <button disabled={emailLoading || emailPage <= 1} onClick={() => loadEmails(emailPage - 1)} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-600">Page {emailPage} of {emailPages || 1} · {emailTotal} messages</span>
                    <button disabled={emailLoading || emailPage >= emailPages} onClick={() => loadEmails(emailPage + 1)} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">Next</button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {selectedUid !== null || emailModalMode === 'compose' ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeEmailModal}>
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl" data-lenis-prevent onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6">
                <h3 className="text-lg font-semibold text-gray-900">{emailModalMode === 'compose' ? 'Compose email' : emailReader?.subject || 'Email'}</h3>
                <button type="button" onClick={closeEmailModal} className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700" aria-label="Close email">✕</button>
              </div>
              {emailModalError && <div className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{emailModalError}</div>}
              {emailModalMode === 'compose' ? (
                <div className="space-y-4 p-4 sm:p-6">
                  <label className="block text-sm font-medium text-gray-700">To<input value={composeTo} onChange={(event) => setComposeTo(event.target.value)} type="email" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <label className="block text-sm font-medium text-gray-700">Subject<input value={composeSubject} onChange={(event) => setComposeSubject(event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <label className="block text-sm font-medium text-gray-700">Body<textarea value={composeText} onChange={(event) => setComposeText(event.target.value)} rows={8} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => void handleEmailDraftSave()} disabled={savingDraft || (!composeTo.trim() && !composeSubject.trim() && !composeText.trim())} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">{savingDraft ? 'Saving…' : 'Save draft'}</button>
                    <button onClick={() => void handleEmailSend()} disabled={composeSending || !composeTo.trim() || !composeText.trim()} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400">{composeSending ? 'Sending…' : 'Send'}</button>
                  </div>
                </div>
              ) : (
                <div>
                  {emailLoading ? <div className="p-6 text-sm text-gray-500">Loading email…</div> : emailReader ? (
                    <div className="p-4 sm:p-6">
                      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div><p className="text-sm text-gray-600">From: {emailReader.from}</p><p className="text-sm text-gray-600">To: {emailReader.to}</p><p className="text-xs text-gray-500">{new Date(emailReader.date).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p></div>
                        <div className="flex gap-2"><button onClick={() => void handleEmailDelete(emailReader.uid)} disabled={deleting} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50">{deleting ? 'Deleting…' : 'Delete'}</button><button onClick={handleOpenEmailWindow} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Open separate window</button></div>
                      </div>
                      <div className="mb-4 whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-4 text-sm leading-6 text-gray-700">{emailReader.body || 'This message has no readable text body.'}</div>
                      <div className="space-y-3"><label className="block text-sm font-medium text-gray-700">Reply<textarea value={replyText} onChange={(event) => setReplyText(event.target.value)} rows={5} placeholder="Type your reply…" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" /></label><div className="flex gap-2"><button onClick={() => void handleEmailDraftSave()} disabled={savingDraft || !replyText.trim()} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">{savingDraft ? 'Saving…' : 'Save draft'}</button><button onClick={() => void handleEmailReply()} disabled={replying || !replyText.trim()} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400">{replying ? 'Sending…' : 'Send reply'}</button></div></div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {activeTab === 'notes' && (
          <section className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6"><h3 className="text-lg font-medium text-gray-900">Notes & Tasks</h3><p className="mt-1 text-sm text-gray-500">Track reminders for this property.</p></div>
            <div className="border-t border-gray-200 p-4 sm:p-6">
              <div className="flex gap-3"><input value={noteInput} onChange={(event) => setNoteInput(event.target.value)} placeholder="Add a note or task…" className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none" onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void handleNoteAdd(); } }} /><button onClick={() => void handleNoteAdd()} disabled={savingNote || !noteInput.trim()} className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400"><Plus className="mr-2 h-4 w-4" />Add</button></div>
              {notesLoading ? <p className="mt-4 text-sm text-gray-500">Loading notes…</p> : <div className="mt-5 space-y-3">{notes.length ? notes.map((note) => <div key={note.id} className="flex items-center justify-between rounded-md border border-gray-200 p-3"><label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(note.done)} onChange={() => void handleNoteToggle(note.id, Boolean(note.done))} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" /><span className={note.done ? 'text-gray-400 line-through' : 'text-gray-700'}>{note.content}</span></label><button onClick={() => void handleNoteDelete(note.id)} className="rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete note"><Trash2 className="h-4 w-4" /></button></div>) : <p className="text-sm text-gray-500">No notes yet.</p>}</div>}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default Dashboard;